class DialogComponent extends HTMLElement {
  #lastWidth = window.innerWidth;

  constructor() {
    super();
    this.dialogButton = null;
    this.dialogModal = null;
    this.closeButton = null;
    this.resizeHandler = null;
    this.animationDuration = null;
  }

  connectedCallback() {
    this.dialogButton = this.querySelector('[data-ref="popup-toggle"]');
    this.dialogModal = this.querySelector('[data-ref="popup-modal"]');
    this.closeButton = this.querySelector('[data-ref="popup-close"]');
    this.controlId = this.id;
    this.externalToggles = this.controlId
      ? this.closest('header-component')?.querySelectorAll(`[data-controls="${this.controlId}"]`) ?? []
      : [];

    this.animationDuration = parseFloat(getComputedStyle(this.closest('.shopify-section')).getPropertyValue('--animation-speed').replace('s', ''));

    if (!this.dialogModal) return;

    // Écouter le clic sur le bouton
    if (this.dialogButton) {
      this.dialogButton.addEventListener('click', this.#showModal.bind(this));
    }

    for (const toggle of this.externalToggles) {
      toggle.addEventListener('click', this.#showModal.bind(this));
    }

    // Écouter le clic sur le bouton de fermeture
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.#closeModal.bind(this));
    }

    // Écouter les événements natifs du dialog
    this.dialogModal.addEventListener('close', this.#onClose.bind(this));
    this.dialogModal.addEventListener('click', this.#onBackdropClick.bind(this));
    this.keydownHandler = this.#onKeyDown.bind(this);
    this.dialogModal.addEventListener('keydown', this.keydownHandler);

    // Écouter le redimensionnement de l'écran
    this.resizeHandler = this.#onResize.bind(this);
    window.addEventListener('resize', this.resizeHandler);
  }

  disconnectedCallback() {
    if (this.dialogButton) {
      this.dialogButton.removeEventListener('click', this.#showModal.bind(this));
    }

    for (const toggle of this.externalToggles ?? []) {
      toggle.removeEventListener('click', this.#showModal.bind(this));
    }

    if (this.dialogModal) {
      this.dialogModal.removeEventListener('close', this.#onClose.bind(this));
      this.dialogModal.removeEventListener('click', this.#onBackdropClick.bind(this));
      if (this.keydownHandler) {
        this.dialogModal.removeEventListener('keydown', this.keydownHandler);
      }
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.#closeModal.bind(this));
    }

    // Nettoyer l'event listener de redimensionnement
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  #showModal(event) {
    if (event) event.preventDefault();

    const isFullscreenSearch = this.dialogModal.classList.contains('dialog-modal--search');

    if (isFullscreenSearch && this.isOpen) {
      this.#closeModal(event);
      return;
    }

    if (isFullscreenSearch) {
      this.dialogModal.show();
      this.setAttribute('data-open', 'true');
      document.body.classList.add('search-open');
      this.#syncSearchAnnouncement();

      for (const toggle of this.externalToggles ?? []) {
        toggle.setAttribute('aria-expanded', 'true');
      }
    } else {
      this.dialogModal.showModal();
    }

    this.dialogModal.scrollTop = 0;
    document.body.classList.add('overflow-hidden');

    if (isFullscreenSearch) {
      this.dialogModal.querySelector('input[name="q"]')?.focus();
    }
  }

  #closeModal(event) {
    if (event) event.preventDefault();
    this.#animateClose();
  }

  #animateClose() {
    if (!this.dialogModal || !this.dialogModal.open) return;

    if (this.dialogModal.classList.contains('dialog-modal--search')) {
      this.dialogModal.close();
      return;
    }

    // Ajouter la classe d'animation de fermeture
    this.dialogModal.classList.add('dialog-modal--closing');

    // Écouter la fin de l'animation
    const handleAnimationEnd = () => {
      this.dialogModal.classList.remove('dialog-modal--closing');
      this.dialogModal.removeEventListener('animationend', handleAnimationEnd);
      this.dialogModal.close();
    };

    this.dialogModal.addEventListener('animationend', handleAnimationEnd);

    // Fallback au cas où l'événement animationend ne se déclenche pas
    setTimeout(() => {
      if (this.dialogModal.classList.contains('dialog-modal--closing')) {
        handleAnimationEnd();
      }
    }, this.animationDuration * 1000); // Fallback après 500ms
  }

  #onClose() {
    this.removeAttribute('data-open');

    for (const toggle of this.externalToggles ?? []) {
      toggle.setAttribute('aria-expanded', 'false');
    }

    this.#clearSearchAnnouncement();
    document.body.classList.remove('overflow-hidden', 'search-open');
  }

  #syncSearchAnnouncement() {
    this.#clearSearchAnnouncement();

    const announcementBar = document.querySelector('.shopify-section-group-header-group .announcement-bar');
    const announcementRect = announcementBar?.getBoundingClientRect();
    const isVisible =
      Boolean(announcementBar) &&
      announcementBar.offsetHeight > 0 &&
      announcementRect?.bottom > 1 &&
      getComputedStyle(announcementBar).display !== 'none';

    if (!isVisible) return;

    const clone = announcementBar.cloneNode(true);
    clone.classList.add('announcement-bar--search-clone');
    clone.setAttribute('aria-hidden', 'true');

    for (const element of clone.querySelectorAll('[id]')) {
      element.removeAttribute('id');
    }

    this.dialogModal.insertBefore(clone, this.dialogModal.firstChild);

    const offset = `${Math.round(clone.offsetHeight)}px`;
    document.documentElement.style.setProperty('--announcement-bar-offset', offset);
    this.closest('.section-header')?.style.setProperty('top', offset);
  }

  #clearSearchAnnouncement() {
    this.dialogModal?.querySelector('.announcement-bar--search-clone')?.remove();
    document.documentElement.style.removeProperty('--announcement-bar-offset');
    this.closest('.section-header')?.style.removeProperty('top');
  }

  #onBackdropClick(event) {
    const rect = this.dialogModal.getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.#animateClose();
    }
  }

  #onKeyDown(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    if (!this.dialogModal.classList.contains('dialog-modal--search')) return;

    this.#closeModal(event);
  }

  #onResize() {
    const currentWidth = window.innerWidth;
    if (currentWidth === this.#lastWidth) return;
    this.#lastWidth = currentWidth;

    if (this.isOpen) {
      this.#animateClose();
    }
  }

  open() {
    this.#showModal();
  }

  close() {
    if (this.dialogModal && this.dialogModal.open) {
      this.#animateClose();
    }
  }

  get isOpen() {
    return this.dialogModal ? this.dialogModal.open : false;
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}
